import { api } from './api';
import { LoginCredentials, AuthTokens, User, UserRole, ApiResponse, RegisterResponseData } from '../types';

class AuthService {
  private readonly TOKEN_KEY = 'auth_tokens';
  private readonly USER_KEY = 'auth_user';

  // API calls
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; tokens: AuthTokens }>> {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  }

  async register(userData: Partial<User>): Promise<ApiResponse<RegisterResponseData>> {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<{ tokens: AuthTokens }>> {
    try {
      const response = await api.post('/auth/refresh', { refreshToken });
      if (!response.data?.success) {
        // Invalid refresh token should immediately log the user out
        this.clearStoredAuth();
        if (response.data?.error === 'Invalid refresh token') {
          throw new Error('Invalid refresh token');
        }
        throw new Error(response.data?.error || 'Token refresh failed');
      }
      return response.data;
    } catch (error: any) {
      this.clearStoredAuth();
      return {
        success: false,
        error: error.response?.data?.error || 'Token refresh failed',
      };
    }
  }

  // User management methods
  async listUsers(limit?: number, paginationToken?: string, search?: string): Promise<ApiResponse<{
    users: User[];
    paginationToken?: string;
    totalCount: number;
  }>> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (paginationToken) params.append('paginationToken', paginationToken);
      if (search) params.append('search', search);
      
      const response = await api.get(`/auth/users?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to list users',
      };
    }
  }

  // Helper to fetch all users (handling pagination automatically)
  async listAllUsers(): Promise<ApiResponse<{ users: User[] }>> {
    try {
      let allUsers: User[] = [];
      let nextToken: string | undefined = undefined;
      
      do {
        const result: ApiResponse<{ users: User[]; paginationToken?: string }> = await this.listUsers(60, nextToken);
        if (!result.success || !result.data) {
          throw new Error(result.error || 'Failed to fetch users');
        }
        
        allUsers = [...allUsers, ...result.data.users];
        nextToken = result.data.paginationToken;
      } while (nextToken);
      
      return {
        success: true,
        data: { users: allUsers, totalCount: allUsers.length } as any
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to list all users'
      };
    }
  }

  async resetPassword(email: string): Promise<ApiResponse<{
    message: string;
    email: string;
  }>> {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send password reset email',
      };
    }
  }

  async confirmPasswordReset(token: string, email: string, newPassword: string): Promise<ApiResponse<{
    message: string;
    email: string;
  }>> {
    try {
      const response = await api.post('/auth/confirm-password-reset', { 
        token, 
        email, 
        newPassword 
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      };
    }
  }

  async updateUserStatus(email: string, enabled: boolean): Promise<ApiResponse<{
    message: string;
    email: string;
    enabled: boolean;
  }>> {
    try {
      const response = await api.put('/auth/user-status', { email, enabled });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user status',
      };
    }
  }

  async updateUserRole(email: string, role: UserRole): Promise<ApiResponse<{
    message: string;
    email: string;
    role: UserRole;
  }>> {
    try {
      const response = await api.put('/auth/user-role', { email, role });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user role',
      };
    }
  }

  async deleteUser(email: string): Promise<ApiResponse<{
    message: string;
    email: string;
  }>> {
    try {
      const response = await api.delete(`/auth/delete-user?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete user',
      };
    }
  }

  // Token management
  storeTokens(tokens: AuthTokens): void {
    localStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokens));
  }

  getStoredTokens(): AuthTokens | null {
    try {
      const tokens = localStorage.getItem(this.TOKEN_KEY);
      return tokens ? JSON.parse(tokens) : null;
    } catch {
      return null;
    }
  }

  storeUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getStoredUser(): User | null {
    try {
      const user = localStorage.getItem(this.USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  }

  clearStoredAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  isTokenValid(token: string): boolean {
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
